import { useState } from "react"
import { api, ApiError } from "../../../lib/api"
import { useSelectedAccount } from "../../../contexts/AccountContext"
import { Member } from "../MembersTab"
import Modal from "../../Modal"

type Props = {
  member: Member
  onClose: () => void
  onChanged: (updated: Member) => void
  onDeleted: (member: Member) => void
}

const ROLE_OPTIONS = [
  { value: "member", label: "メンバー" },
  { value: "owner", label: "オーナー" },
]

const ChangeRoleModal = ({ member, onClose, onChanged, onDeleted }: Props) => {
  const { selectedOrganizationId } = useSelectedAccount()
  const [role, setRole] = useState<Member["role"]>(member.role)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (role === member.role) {
      onClose()
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const data = await api.patch<{ message: string; member: Member }>(
        `/organization_members/${member.id}`,
        { organization_id: selectedOrganizationId, organization_member: { role } }
      )
      onChanged(data.member)
    } catch (err) {
      const { data: errData } = err as ApiError
      setError(errData.error ?? "ロール変更に失敗しました")
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = () => {
    if (!confirm(`${member.name} を組織から削除しますか？`)) return
    onDeleted(member)
  }

  return (
    <Modal title={<><i className="bi bi-pencil" />メンバー編集</>} onClose={onClose}>
      {error && <div className="settings-alert settings-alert--error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-field">
          <label>表示名</label>
          <input type="text" value={member.name} readOnly />
        </div>

        <div className="form-field">
          <label>メールアドレス</label>
          <input type="email" value={member.email} readOnly />
          <p className="form-hint">メールアドレスは変更できません</p>
        </div>

        <div className="form-field">
          <label>ロール</label>
          <select value={role} onChange={e => setRole(e.target.value as Member["role"])}>
            {ROLE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <hr className="settings-divider" />

        <div className="modal__delete-row">
          <span className="modal__delete-label">このメンバーを組織から削除</span>
          <button
            type="button"
            className="btn-danger btn-danger--sm"
            onClick={handleDelete}
          >
            <i className="bi bi-trash" />
            削除
          </button>
        </div>

        <div className="modal__actions">
          <button type="button" className="btn-secondary btn--sm" onClick={onClose}>
            キャンセル
          </button>
          <button type="submit" className="btn-primary btn--sm" disabled={submitting}>
            <i className="bi bi-check-lg" />
            {submitting ? "保存中..." : "変更を保存"}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default ChangeRoleModal
